if (!customElements.get("product-bundle")) {
  class MProductBundle extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        bundleError: ".product-bundles__error",
        productVariantSelects: ".product-option--dropdown-select",
        productBundlesCheck: ".product-bundles__check",
        totalPrice: "[data-total-price]",
        submitBundle: "[data-add-bundle]",
        productImage: "[data-product-image]",
        errorWrapper: ".product-bundles__error",
        cartDrawer: "cart-drawer",
        classify: "[data-classify]",
        quantityInput: ".quantity__input",
      };

      this.domNodes = queryDomNodes(this.selectors, this);
      this.variantsCache = new Map();
      this.allProducts = [];
      this.abortController = null;
    }

    connectedCallback() {
      this.initProductBundles();
    }

    initProductBundles() {
      const products = this.querySelectorAll(".product-bundles__item");

      for (const product of products) {
        const variantsElement = product.nextElementSibling;
        if (!variantsElement?.hasAttribute("data-product-variants")) continue;

        const variants = JSON.parse(variantsElement.innerHTML);
        this.variantsCache.set(product, variants);
        this.allProducts.push(product);

        addEventDelegate({
          context: product,
          event: "change",
          selector: this.selectors.productVariantSelects,
          handler: (e) => this.handleChangePrice(e, product),
        });

        addEventDelegate({
          context: product,
          event: "change",
          selector: this.selectors.productBundlesCheck,
          handler: (e) => {
            this.handleClassifyExclusion(e, product);
            this.handleChangePrice(e, product);
            this.handleChangeImage(e, product);
          },
        });
      }

      addEventDelegate({
        context: this,
        selector: this.selectors.submitBundle,
        handler: (e) => this.handleAddItems(e),
      });
    }

    handleChangePrice(evt, product) {
      const regularPrice = product.querySelector("[data-regular-price]");
      const comparePrice = product.querySelector("[data-compare-price]");
      const savedPrice = product.querySelector("[data-saved-price]");
      const savedPriceWrapper = product.querySelector(".product-bundles__save-price");

      const selectedVariant = evt.target.value;
      const variants = this.variantsCache.get(product);
      if (!variants) return;

      const variant = variants.find((v) => v.id === parseInt(selectedVariant));
      if (!variant) return;

      if (regularPrice) {
        regularPrice.innerHTML = formatMoney(variant.price, HimiSettings.money_format);
        regularPrice.dataset.price = variant.price;
      }

      this.updatePriceDisplay(variant, comparePrice, savedPrice, savedPriceWrapper);
      this.updateTotalPrice(product);
    }

    updateTotalPrice(currentProduct) {
      const selectedVariants = this.allProducts
        .map((product) => {
          const variants = this.variantsCache.get(product);
          const check = product.querySelector('[name="bundleId"]:checked');
          const select = product.querySelector('[name="id"]');

          if (variants && select && check) {
            return variants.find((v) => v.id === Number(select.value));
          }
          return null;
        })
        .filter(Boolean);

      const totalPrice = selectedVariants.reduce((sum, v) => sum + v.price, 0);
      const container = currentProduct.closest(".product-bundles");
      const totalPriceElement = container?.querySelector(this.selectors.totalPrice);

      if (totalPriceElement) {
        totalPriceElement.innerHTML = formatMoney(totalPrice, HimiSettings.money_format);
      }
    }

    handleClassifyExclusion(evt, currentProduct) {
      const checkbox = evt.target;
      if (!checkbox.checked) return;

      const label = currentProduct.querySelector(this.selectors.classify);
      if (!label) return;

      const currentClassify = label.dataset.classify;
      if (!currentClassify) return;

      this.allProducts.forEach((product) => {
        if (product === currentProduct) return;

        const productLabel = product.querySelector(this.selectors.classify);
        if (!productLabel) return;

        const productClassify = productLabel.dataset.classify;
        if (productClassify !== currentClassify) return;

        const productCheckbox = product.querySelector('[name="bundleId"]:checked');
        if (productCheckbox) {
          productCheckbox.checked = false;
          const image = this.querySelector(`[data-product-image="${productCheckbox.id}"]`);
          image?.classList.add("hidden");
        }
      });
    }

    handleChangeImage(evt, product) {
      const selectedVariant = evt.target.id;
      const image = this.querySelector(`[data-product-image="${selectedVariant}"]`);
      if (!image) return;

      const check = product.querySelector('[name="bundleId"]:checked');
      const select = product.querySelector('[name="id"]');

      if (select && check) {
        image.classList.remove("hidden");
      } else {
        image.classList.add("hidden");
      }
    }

    updatePriceDisplay(variant, comparePrice, savedPrice, savedPriceWrapper) {
      const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;

      if (hasDiscount) {
        comparePrice?.classList.remove("hidden");
        if (comparePrice) {
          comparePrice.innerHTML = formatMoney(variant.compare_at_price, HimiSettings.money_format);
        }

        savedPriceWrapper?.classList.remove("hidden");

        if (savedPrice) {
          savedPrice.classList.remove("hidden");
          savedPrice.innerHTML = formatMoney(
            variant.compare_at_price - variant.price,
            HimiSettings.money_format
          );
        }
      } else {
        comparePrice?.classList.add("hidden");
        savedPriceWrapper?.classList.add("hidden");
        savedPrice?.classList.add("hidden");
      }
    }

    handleAddItems(e) {
      e.preventDefault();

      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      const checkedInputs = this.querySelectorAll('[name="bundleId"]:checked');
      if (checkedInputs.length === 0) {
        this.showError("Please select at least one item.", this.domNodes.errorWrapper);
        return;
      }

      const inputIds = [...checkedInputs].map((checkbox) =>
        checkbox.closest("label")?.querySelector('[name="id"]')
      ).filter(Boolean);

      const errorWrapper = this.querySelector(this.selectors.errorWrapper);
      const button = this.querySelector(this.selectors.submitBundle);
      const cartDrawer = document.querySelector(this.selectors.cartDrawer);
      const quantityInput = this.querySelector(this.selectors.quantityInput);
      const quantity = quantityInput?.value || 1;

      const ids = inputIds.map((input) => input.value);
      let data = { items: ids.map((id) => ({ id, quantity: quantity })) };

      if (cartDrawer) {
        data = {
          ...data,
          sections: cartDrawer.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        };
      }

      const config = fetchConfig("javascript");
      config.method = "POST";
      config.body = JSON.stringify(data);
      config.signal = this.abortController.signal;

      this.toggleLoading(true, button);

      fetch(`${HimiSettings.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.showError(response.description || "Failed to add all items to cart!", errorWrapper);
            document.dispatchEvent(
              new CustomEvent("product-ajax:error", {
                detail: { errorMessage: response.description },
              })
            );
            return;
          }
          this.handleSuccess(response, errorWrapper, cartDrawer);
        })
        .catch((err) => {
          if (err.name === "AbortError") return;
          console.error("Error adding items to cart:", err);
          this.showError("An error occurred while adding items to the cart.", errorWrapper);
        })
        .finally(() => {
          this.toggleLoading(false, button);
          this.updateCartDrawerState(cartDrawer);
        });
    }

    handleSuccess(response, errorWrapper, cartDrawer) {
      const { HimiSettings, HimiStrings } = window;

      if (!HimiSettings.use_ajax_atc) {
        window.location = HimiSettings.routes.cart;
        return;
      }

      if (!HimiSettings.enable_cart_drawer) {
        window.HimiTheme.Notification.show({
          target: errorWrapper || document.body,
          method: "appendChild",
          type: "success",
          message: HimiStrings.itemAdded,
          last: 3000,
          sticky: !this.domNodes.errorWrapper,
        });
      } else if (cartDrawer) {
        cartDrawer.renderContents(response);
        HimiTheme.Notification.show({
          target: cartDrawer.querySelector("cart-drawer-items"),
          method: "prepend",
          type: "success",
          message: HimiStrings.itemAdded,
          delay: 400,
        });
      }

      document.dispatchEvent(
        new CustomEvent("product-ajax:added", {
          detail: { product: response },
        })
      );
      window.HimiEvents.emit(HimiTheme.pubSubEvents.cartUpdate, response);
    }

    updateCartDrawerState(cartDrawer) {
      if (!cartDrawer) return;

      const cartDrawerItems = cartDrawer.querySelector("cart-drawer-items");
      cartDrawer.classList.remove("cart--empty");
      cartDrawerItems?.classList.remove("cart--empty");
    }

    showError(err, errorWrapper) {
      if (!errorWrapper) return;

      HimiTheme.Notification.show({
        target: errorWrapper,
        method: "appendChild",
        type: "warning",
        message: err,
      });
    }

    toggleLoading(loading, button) {
      if (!button) return;
      button.classList.toggle("spinner-loading", loading);
    }
  }

  customElements.define("product-bundle", MProductBundle);
}
